<script setup lang="ts">
import type {Byte} from '~/types/legacy'
import type {Populated} from '~/types/populated'
import Tag from '~/components/overflow/tag.vue'
import LikeButton from './like-button.vue'
import {useRelativeTime} from '~/composables/use-relative-date'

const props = defineProps<{
  byte: Populated<Byte>
}>()

const {relativeTimeFormat} = useRelativeTime()
</script>

<template>
  <NuxtLink
    :to="`/technical-bytes/${byte.slug}`"
    class="mx-auto flex flex-col rounded-lg border bg-gray-100 p-4 transition-all hover:bg-gray-200 active:bg-gray-200 sm:p-6 md:p-8"
  >
    <div class="flex h-full items-start gap-3 sm:gap-4">
      <NuxtImg
        v-if="byte.author?.image"
        class="size-8 flex-shrink-0 rounded-full object-cover sm:size-12 sm:h-14 sm:w-14 md:h-16 md:w-16"
        v-bind="resolveMediaProps(byte.author.image)"
      />

      <section class="flex min-w-0 flex-1 flex-col">
        <div class="flex items-start justify-between">
          <p class="truncate text-xs sm:text-sm">Authored by {{ byte.author?.name }}</p>
          <span class="flex-shrink-0 text-xs text-gray-500">
            {{ relativeTimeFormat(new Date(byte.createdAt)) }}
          </span>
        </div>

        <Heading as="h2" size="small" class="mb-2 text-base font-medium leading-tight sm:text-lg">
          {{ byte.title }}
        </Heading>

        <Paragraph
          class="mb-3 line-clamp-2 w-full overflow-hidden text-sm leading-relaxed sm:line-clamp-3 sm:text-base"
          as="p"
        >
          {{ byte.description }}
        </Paragraph>

        <div class="mt-2 flex items-center justify-between">
          <Tag v-if="byte.tags" :tags="byte.tags" />

          <div class="flex items-center gap-6">
            <LikeButton :byte="byte" />

            <div class="flex min-w-0 items-center gap-1.5 text-gray-600 transition-colors hover:text-gray-800">
              <span class="text-xs font-medium lg:text-sm">Read</span>
              <Icon size="18" name="tabler:arrow-up-right" class="flex-shrink-0" />
            </div>
          </div>
        </div>
      </section>
    </div>
  </NuxtLink>
</template>
